import { Grid, InputAdornment, Box, Modal, RadioGroup, FormControlLabel, Radio } from "@mui/material"
import { Currency, PricingRule, sortStageTypes, StageType } from "api/common"
import { RadioField } from "common/RadioField"
import { SumbitButtons } from "common/SubmitButtons"
import { NormalTextField, WhiteTextField } from "common/TextField"
import { RaRecord, useDataProvider, useNotify, useCreate, useRefresh, useRecordContext } from "ra-core"
import { Loading } from "ra-ui-materialui"
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react"
import { PriceList } from "./PricingList"
import { ModalProps } from "./common"

import 'styles.scss'
import { CheckboxField } from "common/CheckboxField"
import RoundedPanel, { Alert } from 'common/RoundedPanel'
import { PricingType } from "api/company"

export interface StageTypeWithId {
  id: string
  value: StageType
}

type TrustMiningSelection = "TM1"|"TM2"
interface TrustMiningProps {
  selected?: TrustMiningSelection,
  setSelected: (e: TrustMiningSelection) => void
  disabled?: boolean
  label?: string
}
const TrustMiningSelectField = ({ selected, setSelected, disabled }: TrustMiningProps) => {
  const record = useRecordContext()
  const stage = record.value as StageType

  if(stage === "TRUST_MINING") {
    return <RadioGroup
      sx={{ justifyContent: 'right' }}
      row
      value={selected}
      onChange={(e: ChangeEvent<HTMLInputElement>) => { 
        setSelected((e.target as HTMLInputElement).value as TrustMiningSelection) 
      }}
    >
      <FormControlLabel value="TM1" control={<Radio size='small' disabled={disabled} />} label="1" />
      <FormControlLabel value="TM2" control={<Radio size='small' disabled={disabled} />} label="2" />
    </RadioGroup>
  }
  return null
}

interface AssetPriceInputProps {
  id: string
  label: string
  value?: number
  onChange: (value: number) => void
  currency: string
}

const AssetPriceInput = ({ id, label, value, onChange, currency }: AssetPriceInputProps) => {

  return (
    <Grid item xs={12}>
      <NormalTextField
        fullWidth
        id={id}
        type="number"
        label={label}
        defaultValue={value}
        onChange={(e) => { onChange(parseFloat(e.target.value)) }}
        InputProps={{
          endAdornment: <InputAdornment position="start">{currency}</InputAdornment>
        }}
        inputProps={{
          step: 0.0001,
          min: 0,
          max: 99
        }}
      />
    </Grid>
  )
}

type CreatePricingRuleModalProps = ModalProps<RaRecord|undefined> & {
  type: PricingType
  currency?: Currency
}

export const CreatePricingRuleModal = ({ open, onClose, record, type, currency = 'EUR' }: CreatePricingRuleModalProps) => {

  const [pricingRules, setPricingRules] = useState<StageTypeWithId[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [newPrice, setNewPrice] = useState<number>(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [trustMining, setTrustMining] = useState<TrustMiningSelection>("TM1")
  const [selectedPricingType, setSelectedPricingType] = useState<'FIXED' | 'FIXED_ASSET_BASED'>('FIXED')
  const [pricePerWordNewAsset, setPricePerWordNewAsset] = useState<number>(0)
  const [pricePerWordRepeatedAsset, setPricePerWordRepeatedAsset] = useState<number>(0)
  const [pricePerWordRecycledAsset, setPricePerWordRecycledAsset] = useState<number>(0)

  const dataProvider = useDataProvider()

  const notify = useNotify()
  const [create] = useCreate()
  const refresh = useRefresh()
  
  const companyId = record?.id
  
  const trustMiningId = pricingRules.find(({ value }) => value == "TRUST_MINING")?.id as string

  const getSelectedList = () => {
    const stages = pricingRules.filter(({ id }) => selected.has(id as string)).map((i) => i.value)
    
    if(trustMining=="TM2" && selected.has(trustMiningId)) {
      stages.push('TRUST_MINING')
    }
    return sortStageTypes(stages)
  }
  
  const createPricingRule = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    const data: any = {
      stages: getSelectedList(),
      companyId: companyId,
      type: type
    }
    
    if (type === 'FIXED' && selectedPricingType === 'FIXED_ASSET_BASED') {
      data.currency = currency
      data.pricePerWordNewAsset = pricePerWordNewAsset
      data.pricePerWordRepeatedAsset = pricePerWordRepeatedAsset
      data.pricePerWordRecycledAsset = pricePerWordRecycledAsset
      data.type = 'FIXED_ASSET_BASED'
      data.price = pricePerWordNewAsset
    } else if (type === 'FIXED') {
      data.price = pricePerWordNewAsset
      data.currency = currency
    } else {
      data.price = newPrice
    }
    
    create('pricingrules',
      { data },
      {
        onSuccess: () => {
          notify('Price rule was successfully created.', { type: 'success' })
          refresh()
          onClose()
        },
        onError: (error: any) => {
          refresh()
          notify(error?.message ?? 'Something went wrong.', { type: 'error' })
          console.log(JSON.stringify(error))
        }
      }
    )
  }

  useEffect(() => {
    dataProvider
      .getList("pricingrules/stage-types", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
        filter: { companyId: companyId }
      })
      .then(({ data }) => {
        setPricingRules(data as StageTypeWithId[])
      })
      .catch(() => {
        console.warn('Something went wrong during fetching pricing rules.')
      })
      .finally(() => {
        setIsLoading(false) 
      })
  }, [open])

  const formatNumber = (num?: number, prec?: number) => {
    return num ? parseFloat(num.toFixed(prec||5)) : undefined
  }
  
  const CurrentPriceList = () => {
    return <PriceList isSingle={true} companyId={companyId}>
      <TrustMiningSelectField
        selected={selected?.has(trustMiningId) ? trustMining : undefined}
        disabled={!(selected?.has(trustMiningId))}
        setSelected={(e) => { setTrustMining(e) }}
      />
      <CheckboxField
        selected={selected}
        label="Select"
        size='small'
        setSelected={(e) => { setSelected(e) }}
      />
    </PriceList>
  }
  
  const addForm = <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: "100%" }}>
    <form
      onSubmit={createPricingRule}
    >
      <Grid container spacing="14px">
        {type === 'FIXED' ? (
          <>
            <Grid item xs={12}>
              <NormalTextField
                fullWidth
                id="pricingType"
                select
                label="Pricing Type"
                value={selectedPricingType}
                onChange={(e) => { setSelectedPricingType(e.target.value as 'FIXED' | 'FIXED_ASSET_BASED') }}
                SelectProps={{
                  native: true
                }}
              >
                <option value="FIXED">Standard</option>
                <option value="FIXED_ASSET_BASED">Asset-Based (Extended)</option>
              </NormalTextField>
            </Grid>
            <AssetPriceInput
              id="pricePerWordNewAsset"
              label={selectedPricingType === 'FIXED_ASSET_BASED' ? "Price per word (New Asset)" : "Price per word"}
              onChange={setPricePerWordNewAsset}
              currency={currency}
            />
            {selectedPricingType === 'FIXED_ASSET_BASED' && (
              <>
                <AssetPriceInput
                  id="pricePerWordRepeatedAsset"
                  label="Price per word (Repeated Asset)"
                  onChange={setPricePerWordRepeatedAsset}
                  currency={currency}
                />
                <AssetPriceInput
                  id="pricePerWordRecycledAsset"
                  label="Price per word (Recycled Asset)"
                  onChange={setPricePerWordRecycledAsset}
                  currency={currency}
                />
              </>
            )}
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <NormalTextField
                fullWidth
                id="pricePerWord"
                type="number"
                label="Price per word"
                onChange={(e) => { setNewPrice(parseFloat(e.target.value)) }}
                InputProps={{
                  endAdornment: <InputAdornment position="start">{currency}</InputAdornment>
                }}
                inputProps={{
                  step: 0.0001,
                  min: 0,
                  max: 99
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Box height={32} />
      <SumbitButtons
        onClose={onClose}
      />
    </form>
  </Grid>
  
  return <Modal
    open={open}
    onClose={onClose}
    disableAutoFocus={true}
  >
    <RoundedPanel modal columns width="1100px">
      <Box width="620px">
        {isLoading ? <Loading /> : <CurrentPriceList/>}
      </Box>
      <Box width="415px">
        {addForm}
      </Box>
    </RoundedPanel>
  </Modal>
}